import { useExperimentTreatment } from '@ms/yammer-experiments-context';
import type { ITheme } from '@ms/yammer-web-core-ui/dist/util/theme';
import { loadCreateCoreUIV8ThemeFromV9 } from '@ms/yammer-web-core-ui/dist/util/theme';
import type { OverridableCoreUITokens, Theme } from '@ms/yammer-web-core-ui/dist/util/theme/fluent9';
import { useFluent9Theme } from '@ms/yammer-web-core-ui/dist/util/theme/fluent9';
import { useEffect, useRef, useState } from 'react';

import { useBrandTheme } from '../store';

type CurrentActiveThemeV9 = Theme | undefined;
type UseGetActiveNetworkBrandTheme = (fallbackTheme: ITheme) => {
  readonly isCustomThemingEnabled: boolean;
  readonly currentActiveTheme: ITheme;
  readonly currentActiveThemeCoreUITokenOverrides: OverridableCoreUITokens;
  readonly currentActiveThemeV9: CurrentActiveThemeV9;
};

export const useGetActiveNetworkBrandTheme: UseGetActiveNetworkBrandTheme = (fallbackTheme) => {
  const isCustomThemingEnabled = useExperimentTreatment('EngageCustomBrandingTheming') === true;
  const currentNetworkBrandTheme = useBrandTheme();
  const fallbackV9Theme = useFluent9Theme(fallbackTheme);
  const [currentActiveTheme, setCurrentActiveTheme] = useState<ITheme>(fallbackTheme);
  const [currentActiveThemeCoreUITokenOverrides, setCurrentActiveThemeCoreUITokenOverrides] =
    useState<OverridableCoreUITokens>({});
  const [currentActiveV9Theme, setCurrentActiveV9Theme] = useState<CurrentActiveThemeV9>(undefined);
  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;

    const setCurrentActiveThemeToDefault = () => {
      setCurrentActiveV9Theme(fallbackV9Theme);
      setCurrentActiveTheme(fallbackTheme);
      setCurrentActiveThemeCoreUITokenOverrides({});
    };

    /* istanbul ignore next */
    const updateTheme = async () => {
      if (isCustomThemingEnabled && currentNetworkBrandTheme && currentNetworkBrandTheme.tokensAndStyles) {
        try {
          const { createCoreUIV8ThemeFromV9 } = await loadCreateCoreUIV8ThemeFromV9();
          if (!isMountedRef.current) {
            return;
          }
          const isDarkMode = !!fallbackTheme?.isInverted;
          const v9Theme = !isDarkMode
            ? currentNetworkBrandTheme.tokensAndStyles.light
            : currentNetworkBrandTheme.tokensAndStyles.dark;
          const themeMode = !isDarkMode ? 'light' : 'dark';
          const newTheme = createCoreUIV8ThemeFromV9(v9Theme, themeMode);
          if (isMountedRef.current) {
            setCurrentActiveV9Theme(v9Theme);
            setCurrentActiveTheme(newTheme);
            setCurrentActiveThemeCoreUITokenOverrides({
              colorThemeDark: newTheme.palette.themeSecondary,
            });
          }
        } catch {
          if (isMountedRef.current) {
            setCurrentActiveThemeToDefault();
          }
        }
      } else {
        if (isMountedRef.current) {
          setCurrentActiveThemeToDefault();
        }
      }
    };

    updateTheme();

    return () => {
      isMountedRef.current = false;
    };
  }, [isCustomThemingEnabled, currentNetworkBrandTheme, fallbackTheme, fallbackV9Theme]);

  return {
    isCustomThemingEnabled,
    currentActiveTheme,
    currentActiveThemeCoreUITokenOverrides,
    currentActiveThemeV9: currentActiveV9Theme,
  };
};
